// src/modules/smelter/api.js
import API from '../../services/api';

// ── Smelters ──
export const fetchSmelters = ({ search = '', active } = {}) =>
  API.get('/smelters', {
    params: { search, ...(active !== undefined ? { active } : {}) },
  }).then((r) => r.data);

export const getSmelterById = (id) =>
  API.get(`/smelters/${id}`).then((r) => r.data);

export const createSmelter = (data) =>
  API.post('/smelters', data).then((r) => r.data);

export const updateSmelter = (id, data) =>
  API.put(`/smelters/${id}`, data).then((r) => r.data);

export const deleteSmelter = (id) =>
  API.delete(`/smelters/${id}`).then((r) => r.data);

// ── Smelter Dispatches ──
export const fetchDispatches = ({ page = 1, limit = 10, status = '', smelterId = '' } = {}) =>
  API.get('/smelter-dispatches', {
    params: { page, limit, ...(status ? { status } : {}), ...(smelterId ? { smelterId } : {}) },
  }).then((r) => r.data);

export const getDispatchById = (id) =>
  API.get(`/smelter-dispatches/${id}`).then((r) => r.data);

export const createDispatch = (data) =>
  API.post('/smelter-dispatches', data).then((r) => r.data);

export const deleteDispatch = (id) =>
  API.delete(`/smelter-dispatches/${id}`).then((r) => r.data);

export const confirmReceipt = (id, data) =>
  API.patch(`/smelter-dispatches/${id}/confirm-receipt`, data).then((r) => r.data);

export const overrideTotalAmount = (id, data) =>
  API.patch(`/smelter-dispatches/${id}/confirm-receipt/override-amount`, data).then((r) => r.data);

export const recordPayment = (id, data) =>
  API.patch(`/smelter-dispatches/${id}/record-payment`, data).then((r) => r.data);

// Undo the most recent instalment on a dispatch that has not settled yet.
export const reverseLastPaymentReceipt = (id) =>
  API.delete(`/smelter-dispatches/${id}/payment-receipts/last`).then((r) => r.data);

// ── Smelter Sale (Tax) Invoice ──
export const fetchDeliveryEmployees = () =>
  API.get('/smelter-dispatches/meta/delivery-employees').then((r) => r.data);

// Non-consuming preview of the next AGS invoice number (editable pre-fill).
export const fetchNextInvoiceNo = (date) =>
  API.get('/smelter-dispatches/meta/next-invoice-no', {
    params: date ? { date } : {},
  }).then((r) => r.data);

// GST rate in force for metal sales. The Confirm Receipt preview backs GST out of
// the smelter's inclusive total with this, so it matches the invoice raised later.
export const fetchGoldGstRate = () =>
  API.get('/smelter-dispatches/meta/gst-rate').then((r) => r.data);

// Returns { success, data }; rejects with 404 when no invoice has been generated yet.
export const getSmelterInvoice = (id) =>
  API.get(`/smelter-dispatches/${id}/invoice`).then((r) => r.data);

export const generateSmelterInvoice = (id, data) =>
  API.post(`/smelter-dispatches/${id}/invoice`, data).then((r) => r.data);

// Downloads the invoice PDF as a blob and triggers a browser download.
// `copy` ('original' | 'duplicate') stamps ORIGINAL BILL / DUPLICATE BILL on top.
export const downloadSmelterInvoicePdf = async (id, invoiceNo, copy) => {
  const res = await API.get(`/smelter-dispatches/${id}/invoice/pdf`, {
    responseType: 'blob',
    params: copy ? { copy } : undefined,
  });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  const suffix = copy === 'duplicate' ? '_Duplicate' : copy === 'original' ? '_Original' : '';
  link.setAttribute('download', `Invoice_${invoiceNo || id}${suffix}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

// Generates a fly invoice PDF (no DB save) as a blob and triggers a browser download.
export const generateFlyInvoicePdf = async (payload, invoiceNo) => {
  const res = await API.post(`/smelter-dispatches/fly-invoice/pdf`, payload, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', `Invoice_${invoiceNo || 'FLY'}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

// ── Smelter GST Recovery Ledger ──
// GST is charged inside the smelter's GST-inclusive payment; we deposit ("fill") it
// with the govt and the smelter returns/adjusts it on a later transaction.
export const fetchGstLedgerSummaries = () =>
  API.get('/smelter-gst-ledger').then((r) => r.data);

export const fetchSmelterGstLedger = (smelterId) =>
  API.get(`/smelter-gst-ledger/${smelterId}`).then((r) => r.data);

export const fetchGstLinkableDispatches = (smelterId) =>
  API.get(`/smelter-gst-ledger/${smelterId}/dispatches`).then((r) => r.data);

export const addGstCharged = (smelterId, data) =>
  API.post(`/smelter-gst-ledger/${smelterId}/charged`, data).then((r) => r.data);

export const addGstReturn = (smelterId, data) =>
  API.post(`/smelter-gst-ledger/${smelterId}/return`, data).then((r) => r.data);

export const markGstEntryFiled = (entryId, data) =>
  API.patch(`/smelter-gst-ledger/entry/${entryId}/filed`, data).then((r) => r.data);

export const updateGstReturn = (entryId, data) =>
  API.patch(`/smelter-gst-ledger/entry/${entryId}`, data).then((r) => r.data);

export const deleteGstReturn = (entryId) =>
  API.delete(`/smelter-gst-ledger/entry/${entryId}`).then((r) => r.data);

// ── Smelter Performance ──
export const getSmelterPerformance = (params = {}) =>
  API.get('/smelters/performance', { params }).then((r) => r.data);

export const getMonthlyTrend = (params = {}) =>
  API.get('/smelters/performance/monthly-trend', { params }).then((r) => r.data);

export const getSmelterStats = (id) =>
  API.get(`/smelters/${id}/performance`).then((r) => r.data);

// ── Gold Transit Memo (AGTM) ──
// The physical memo carried with the metal: who carries it, what they carry, and
// where to. Independent of the tax invoice, which is raised only after receipt.
export const fetchMemoEmployees = () =>
  API.get('/smelter-dispatches/meta/memo-employees').then((r) => r.data);

// Next (non-consuming) memo number + company origin/signatory/text defaults.
export const fetchMemoDefaults = (date) =>
  API.get('/smelter-dispatches/meta/memo-defaults', {
    params: date ? { date } : {},
  }).then((r) => r.data);

export const fetchTransitMemos = ({ page = 1, limit = 20, search = '', smelterId = '', dispatchId = '' } = {}) =>
  API.get('/smelter-dispatches/transit-memos', {
    params: {
      page, limit,
      ...(search ? { search } : {}),
      ...(smelterId ? { smelterId } : {}),
      ...(dispatchId ? { dispatchId } : {}),
    },
  }).then((r) => r.data);

export const getTransitMemo = (memoId) =>
  API.get(`/smelter-dispatches/transit-memos/${memoId}`).then((r) => r.data);

export const createTransitMemo = (data) =>
  API.post('/smelter-dispatches/transit-memos', data).then((r) => r.data);

export const updateTransitMemo = (memoId, data) =>
  API.put(`/smelter-dispatches/transit-memos/${memoId}`, data).then((r) => r.data);

export const deleteTransitMemo = (memoId) =>
  API.delete(`/smelter-dispatches/transit-memos/${memoId}`).then((r) => r.data);

// Shared blob → browser download.
const saveBlob = (res, filename) => {
  const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

export const downloadTransitMemoPdf = async (memoId, memoNo) => {
  const res = await API.get(`/smelter-dispatches/transit-memos/${memoId}/pdf`, { responseType: 'blob' });
  saveBlob(res, `TransitMemo_${String(memoNo || memoId).replace(/[^\w.-]+/g, '_')}.pdf`);
};

// Renders the memo straight from the form — nothing saved, no number consumed.
export const previewTransitMemoPdf = async (payload) => {
  const res = await API.post('/smelter-dispatches/transit-memos/preview-pdf', payload, { responseType: 'blob' });
  saveBlob(res, `TransitMemo_${String(payload?.memoNo || 'PREVIEW').replace(/[^\w.-]+/g, '_')}.pdf`);
};
